import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import {
  UnifiedThemeProvider,
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  pageTheme as stockPageThemes,
  palettes,
  shapes,
} from '@backstage/theme';
import DarkIcon from '@material-ui/icons/Brightness2';
import LightIcon from '@material-ui/icons/WbSunny';

// The palette of the sign-in page, for the older Material UI components.
// Backstage UI components take the same colours from ./bui.css; docs/branding.md
// lists what each one is for.
const RED = '#b41618';
// Red as text or a marker on a dark ground, where Dotify red is too dark to read.
const RED_ON_DARK = '#e5484d';

// Backstage gives each kind of page and template its own bright gradient (pink for
// documentation, purple for tools, teal for services). One quiet band replaces them all.
const band = genPageTheme({
  colors: ['#19191c', '#3a1213'],
  shape: shapes.wave,
});
const pageTheme = Object.fromEntries(
  Object.keys(stockPageThemes).map(name => [name, band]),
);

// The sidebar is dark in both themes.
const navigation = {
  background: '#0c0c0e',
  indicator: RED_ON_DARK,
  color: '#c3c3c8',
  selectedColor: '#ffffff',
  navItem: { hoverBackground: '#1c1c20' },
  submenu: { background: '#19191c' },
};

const light = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      background: { default: '#f7f6f6', paper: '#ffffff' },
      text: { primary: '#1b1b1f', secondary: '#55555c' },
      primary: { main: RED },
      secondary: { main: RED },
      link: RED,
      linkHover: '#951214',
      border: '#e2dfdf',
      divider: '#e2dfdf',
      textSubtle: '#55555c',
      navigation: { ...navigation, background: '#111113' },
      tabbar: { indicator: RED },
    },
  }),
  pageTheme,
});

const dark = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      background: { default: '#111113', paper: '#19191c' },
      text: { primary: '#f2f2f3', secondary: '#c3c3c8' },
      primary: { main: RED_ON_DARK, contrastText: '#ffffff' },
      secondary: { main: RED_ON_DARK },
      link: RED_ON_DARK,
      linkHover: '#f2555a',
      border: '#2a2a2f',
      divider: '#2a2a2f',
      textContrast: '#ffffff',
      textSubtle: '#c3c3c8',
      textVerySubtle: '#6f6f78',
      navigation,
      tabbar: { indicator: RED_ON_DARK },
    },
  }),
  pageTheme,
  components: {
    // Filled buttons keep Dotify red with white text, as the Backstage UI ones do.
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          backgroundColor: RED,
          color: '#ffffff',
          '&:hover': { backgroundColor: '#c91c1f' },
        },
      },
    },
  },
});

// Same names as the built-in themes, so these replace them.
export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ThemeBlueprint.make({
      name: 'light',
      params: {
        theme: {
          id: 'light',
          title: 'Light Theme',
          variant: 'light',
          icon: <LightIcon />,
          Provider: ({ children }) => (
            <UnifiedThemeProvider theme={light} children={children} />
          ),
        },
      },
    }),
    ThemeBlueprint.make({
      name: 'dark',
      params: {
        theme: {
          id: 'dark',
          title: 'Dark Theme',
          variant: 'dark',
          icon: <DarkIcon />,
          Provider: ({ children }) => (
            <UnifiedThemeProvider theme={dark} children={children} />
          ),
        },
      },
    }),
  ],
});
